import { Tooltip as ChakraTooltip, Portal } from '@chakra-ui/react'
import React from 'react'

// Touch-capable devices can't open Ark tooltips by hovering, so we add a
// tap-to-toggle below. On those devices the tap's synthetic pointerdown/click
// would otherwise immediately close what the tap opened, so Ark's own
// close-on-pointerdown/click is disabled -- but only here, so non-touch-capable
// devices keep the normal click-to-dismiss behavior. (This is a capability
// check, not an input check: mouse users on hybrid devices such as a Surface
// Pro also lose click-to-dismiss; hover-off still closes the tooltip.)
const isTouchDevice =
  typeof navigator !== 'undefined' &&
  (navigator.maxTouchPoints > 0 ||
    (typeof window !== 'undefined' && 'ontouchstart' in window))

export interface TooltipProps {
  content: React.ReactNode
  children: React.ReactElement
  showArrow?: boolean
  disabled?: boolean
  positioning?: {
    placement?: string
  }
  portalled?: boolean
  openDelay?: number
  closeDelay?: number
}

export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(
  function TooltipComponent(
    {
      children,
      content,
      showArrow,
      disabled,
      positioning,
      portalled = true,
      openDelay,
      closeDelay,
      ...rest
    },
    ref,
  ) {
    // Controlled so the tooltip can be opened by a tap on touch devices.
    // Ark/Zag tooltips intentionally ignore touch pointers and only open on
    // hover or keyboard focus, leaving them unreachable on mobile (issue
    // #3475). Driving `open` ourselves lets us keep the native hover/focus
    // behavior (via onOpenChange) and add a tap-to-toggle on top.
    const [open, setOpen] = React.useState(false)

    // If `disabled` flips on (or `content` empties) while the tooltip is
    // visible, the early returns below unmount ChakraTooltip.Root and Ark
    // never fires onOpenChange({ open: false }), leaving `open` stuck at
    // true -- the tooltip would then reappear on its own once re-enabled.
    React.useEffect(() => {
      if (disabled || content == null || content === '') setOpen(false)
    }, [disabled, content])

    // A tap fires pointerdown -> pointerup -> click. We toggle on pointerup
    // (which carries pointerType) and disable Ark's own click/pointerdown
    // close handlers below, otherwise the synthetic click immediately closes
    // what the tap just opened.
    const handleTriggerPointerUp = (event: React.PointerEvent) => {
      if (event.pointerType === 'touch') {
        setOpen((prev) => !prev)
      }
    }

    if (disabled) return <>{children}</>

    // No content -> no tooltip. Prevents empty bubbles when callers pass an
    // optional/undefined label (issue #3756 item 4).
    if (content == null || content === '') return <>{children}</>

    return (
      <ChakraTooltip.Root
        open={open}
        onOpenChange={(e) => setOpen(e.open)}
        openDelay={openDelay}
        closeDelay={closeDelay}
        // On touch-capable devices the tap-to-toggle owns open/close; without
        // these, Ark closes the tooltip on the tap's pointerdown/click and it
        // never stays visible. Left at Ark defaults on non-touch-capable
        // devices, which keep click-to-dismiss (hybrids count as touch-capable
        // even when a mouse is in use, since isTouchDevice is a capability
        // check).
        closeOnPointerDown={isTouchDevice ? false : undefined}
        closeOnClick={isTouchDevice ? false : undefined}
      >
        <ChakraTooltip.Trigger asChild onPointerUp={handleTriggerPointerUp}>
          {children}
        </ChakraTooltip.Trigger>
        {portalled ? (
          <Portal>
            <ChakraTooltip.Positioner>
              <ChakraTooltip.Content ref={ref} {...rest}>
                {showArrow && (
                  <ChakraTooltip.Arrow>
                    <ChakraTooltip.ArrowTip />
                  </ChakraTooltip.Arrow>
                )}
                {content}
              </ChakraTooltip.Content>
            </ChakraTooltip.Positioner>
          </Portal>
        ) : (
          <ChakraTooltip.Positioner>
            <ChakraTooltip.Content ref={ref} {...rest}>
              {showArrow && (
                <ChakraTooltip.Arrow>
                  <ChakraTooltip.ArrowTip />
                </ChakraTooltip.Arrow>
              )}
              {content}
            </ChakraTooltip.Content>
          </ChakraTooltip.Positioner>
        )}
      </ChakraTooltip.Root>
    )
  },
)
